import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import TournamentWhotGame from '../components/tournament-whot/TournamentWhotGame';
import PlatformConfirmModal from '../components/PlatformConfirmModal';

import { getAccessToken, clearAuthStorage } from '../services/api';
const SOCKET_URL = window.location.origin;

export default function TournamentPlay() {
  const { id: tournamentId } = useParams();
  const navigate = useNavigate();
  const token = getAccessToken();
  const [tournament, setTournament] = useState(null);
  const [playId, setPlayId] = useState(null);
  const [playNumber, setPlayNumber] = useState(null);
  const [playsRemaining, setPlaysRemaining] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [status, setStatus] = useState('loading');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(false);
  const [shapeSelection, setShapeSelection] = useState(null);
  const [emotes, setEmotes] = useState({});
  const [showForfeitConfirm, setShowForfeitConfirm] = useState(false);
  const socketRef = useRef(null);

  const loadLeaderboard = useCallback(async () => {
    try {
      const r = await api.tournaments.getLeaderboard(tournamentId);
      setPlaysRemaining(prev => r.data?.data?.yourPlaysRemaining !== undefined ? r.data.data.yourPlaysRemaining : prev);
    } catch (_) {}
  }, [tournamentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await api.tournaments.getById(tournamentId);
        if (cancelled) return;
        const data = t.data?.data || t.data?.tournament;
        if (!data || data.game_type !== 'whot') throw new Error('This is not a Whot tournament.');
        if (data.status !== 'live') throw new Error('This tournament is not currently live.');
        setTournament(data);
        const p = await api.tournaments.startPlay(tournamentId);
        const pd = p.data?.data || p.data;
        setPlayId(pd.playId || pd.play?.id);
        setPlayNumber(pd.playNumber || pd.play?.play_number);
        setPlaysRemaining(pd.playsRemaining);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) {
          // A 409 with an existing play is an authoritative resume response,
          // not a duplicate-payment failure. Reuse that play; never start or
          // charge another play during reconnect/navigation recovery.
          const existingPlayId = e.response?.status === 409 ? e.response?.data?.playId : null;
          if (existingPlayId) {
            setPlayId(existingPlayId);
            setPlayNumber(e.response?.data?.playNumber || null);
            setStatus('ready');
            return;
          }
          setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Unable to start tournament play');
          setStatus('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tournamentId]);

  useEffect(() => {
    if (!playId || !tournament) return;
    const socket = io(`${SOCKET_URL}/tournament-whot`, {
      auth: { token },
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('startPlay', { tournamentId, playId }));
    socket.on('gameState', state => {
      setGameState({ ...state, _receivedAt: Date.now() });
      if (state?.pendingShapeSelection) setShapeSelection(prev => prev || { cardId: state.topCard?.id || null });
      else setShapeSelection(null);
      setStatus('playing');
      setPendingAction(false);
    });
    socket.on('cardsDrawn', () => setPendingAction(false));
    socket.on('actionResult', () => setPendingAction(false));
    socket.on('shapeSelectionRequired', data => { setShapeSelection(data); setPendingAction(false); });
    socket.on('actionRejected', data => {
      setPendingAction(false);
      if (data.reason !== 'duplicate_action') setError(`Action rejected: ${data.reason}`);
    });
    socket.on('emoteReceived', data => setEmotes(prev => ({ ...prev, [data.userId]: data })));
    socket.on('missedTurn', data => setGameState(prev => prev ? { ...prev, missedTurns: data.missedTurns, _receivedAt: Date.now() } : prev));
    socket.on('playEnded', async data => {
      setResult(data);
      setGameState(prev => prev ? { ...prev, state: 'finished', status: data?.status, _receivedAt: Date.now() } : prev);
      setStatus('finished');
      setPendingAction(false);
      await loadLeaderboard();
      if (Number(data?.playNumber || playNumber) >= Number(tournament?.max_plays_per_player || 50)) navigate(`/tournaments/${tournamentId}?completed=1`, { replace: true });
    });
    socket.on('forfeited', data => {
      setResult(data);
      setStatus('finished');
      setPendingAction(false);
      loadLeaderboard();
    });
    socket.on('timeUp', data => {
      setResult(data);
      setStatus('finished');
      setPendingAction(false);
      loadLeaderboard();
    });
    socket.on('tournamentCancelled', data => {
      setResult({ status: 'cancelled', reason: data?.reason || 'Tournament cancelled.' });
      setStatus('finished');
      setPendingAction(false);
    });
    socket.on('error', data => {
      setPendingAction(false);
      setError(data.message || 'Tournament action failed');
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [playId, tournament, tournamentId, token, loadLeaderboard, navigate]);

  const sendAction = (type, payload = {}) => {
    if (!socketRef.current || !playId || pendingAction || gameState?.state !== 'playing') return;
    setPendingAction(true);
    socketRef.current.emit(type, {
      ...payload,
      playId,
      actionId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
    });
  };

  const handlePlayCard = cardId => sendAction('playCard', { cardId });
  const handleShapeSelect = shape => {
    if (!shapeSelection || pendingAction) return;
    setPendingAction(true);
    socketRef.current?.emit('selectShape', {
      playId,
      shape,
      actionId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
    });
  };
  const handleDrawCard = () => sendAction('drawCard');
  const forfeit = () => setShowForfeitConfirm(true);
  const confirmForfeit = () => {
    setShowForfeitConfirm(false);
    socketRef.current?.emit('resign', { playId });
  };

  if (status === 'loading' || status === 'ready') return <div style={styles.center}><h2>Starting Whot Tournament…</h2><p>Preparing Play {playNumber || ''}</p></div>;
  if (status === 'error') return <div style={styles.center}><h2>Unable to start</h2><p>{error}</p><button style={styles.button} onClick={() => navigate(`/tournaments/${tournamentId}`)}>Back to Tournament</button></div>;
  if (status === 'finished') return <TournamentWhotGame playId={playId} gameState={{ ...gameState, state: 'finished' }} tournamentName={tournament?.name} playNumber={playNumber} playsRemaining={playsRemaining} maxPlaysPerPlayer={tournament?.max_plays_per_player} maxActionsPerGame={tournament?.max_actions_per_game} socket={socketRef.current} emotes={emotes} pendingAction={false} shapeSelection={shapeSelection} onShapeSelect={handleShapeSelect} result={result} onAgain={() => window.location.reload()} onLobby={() => navigate(`/tournaments/${tournamentId}`, { replace: true })} onForfeit={() => {}} onPlayCard={() => {}} onDrawCard={() => {}} />;
  if (!gameState) return <div style={styles.center}><h2>Starting Whot Tournament…</h2><p>Preparing Play {playNumber || ''}</p></div>;

  return <>
    {showForfeitConfirm && (
      <PlatformConfirmModal
        title="Forfeit Tournament Play?"
        message="Are you sure you want to forfeit this tournament play?"
        cancelLabel="Keep Playing"
        confirmLabel="Forfeit Play"
        onCancel={() => setShowForfeitConfirm(false)}
        onConfirm={confirmForfeit}
      />
    )}
    <TournamentWhotGame playId={playId} gameState={{ ...gameState, tournamentName: tournament?.name }} tournamentName={tournament?.name} playNumber={playNumber} playsRemaining={playsRemaining} maxPlaysPerPlayer={tournament?.max_plays_per_player} maxActionsPerGame={tournament?.max_actions_per_game} socket={socketRef.current} emotes={emotes} pendingAction={pendingAction} shapeSelection={shapeSelection} onShapeSelect={handleShapeSelect} onPlayCard={handlePlayCard} onDrawCard={handleDrawCard} onForfeit={forfeit} result={result} onAgain={() => window.location.reload()} onLobby={() => navigate(`/tournaments/${tournamentId}`, { replace: true })} />
  </>;
}

const styles = { center: { minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20, background: '#0a0a1a', color: '#fff', textAlign: 'center' }, button: { padding: '12px 22px', border: 0, borderRadius: 9, background: '#f59e0b', color: '#111', fontWeight: 700, cursor: 'pointer' } };
